import React from "react";
import t from "../../../i18n";
import { minutesToFractional } from "../../../lib/time/minutes";
import type fr from "./fr";
import type {
  ActivityIndexJSON,
  CategoryActivityType,
  CategoryProjectActivityType,
} from "../../../api/useActivities";
import {
  getEventStatusText,
  type DenormalizedEventStatus,
} from "../../Project/Events/utils/faultEventStatus";
import type { Translation } from "../../../api/useTranslations";
import type { Reparability } from "../../../api/Reparability";
import type { PriorityFaultEvent } from "diagnostics-domain/faultEvent";
import type { CustomTranslations } from "../../../i18n/types";
import { getEventPriorityText } from "../../Project/Events/utils/event";

const en: typeof fr = {
  "action.confirm": "Confirm",
  "activity.analysis.manual_failure_changed.analyses_added.subtitle"({ count }: { count: number }) {
    return (
      <>
        Addition on {count} {count < 2 ? "analysis" : "analyses"}
      </>
    );
  },
  "activity.analysis.manual_failure_changed.analyses_deleted.subtitle"({
    count,
  }: {
    count: number;
  }) {
    return (
      <>
        Deletion on {count} {count < 2 ? "analysis" : "analyses"}
      </>
    );
  },
  "activity.analysis.manual_failure_changed.analyses_edited_impacts.subtitle"({
    count,
    impacts,
  }: {
    count: number;
    impacts: { name: string; previous: number; current: number }[];
  }) {
    return (
      <ul>
        {impacts.map(
          impact =>
            impact.previous !== impact.current && (
              <li key={impact.name}>
                Modification of impact on {impact.name} from {Math.abs(impact.previous)} to{" "}
                {Math.abs(impact.current)} on {count} {count < 2 ? "analysis" : "analyses"}
              </li>
            ),
        )}
      </ul>
    );
  },
  "activity.analysis.manual_failure_changed.analyses_edited_reparability.subtitle"({
    count,
    reparability,
    translation,
  }: {
    count: number;
    reparability: { previous: Reparability | null; current: Reparability | null };
    translation: {
      FR: {
        REPARABILITY_0: string;
        REPARABILITY_1: string;
        REPARABILITY_2: string;
      };
      EN: {
        REPARABILITY_0: string;
        REPARABILITY_1: string;
        REPARABILITY_2: string;
      };
    };
  }) {
    return (
      <>
        Modification of outage time for repair from{" "}
        {reparability.previous
          ? en["reparability"](reparability.previous, translation)
          : t("reparability.none")}
        to{" "}
        {reparability.current
          ? en["reparability"](reparability.current, translation)
          : t("reparability.none")}{" "}
        on {count} {count < 2 ? "analysis" : "analyses"}
      </>
    );
  },
  "activity.analysis.manual_failure_changed.title"({
    authorName,
    globalImpactedAnalysesCount,
    failureLink,
  }: {
    authorName: React.ReactNode;
    globalImpactedAnalysesCount: number;
    failureLink: React.ReactNode;
  }) {
    return (
      <>
        {authorName} edited {failureLink} on {globalImpactedAnalysesCount}
        {globalImpactedAnalysesCount < 2 ? " analysis" : " analyses"}
      </>
    );
  },
  "activity.badge.setup.deleted": "Setup deleted",
  "activity.badge.setup.setting": "setup",
  "activity.common.empty_field": "Empty field",
  "activity.description.cross_filters_deleted.title": ({
    authorName,
  }: {
    authorName: React.ReactNode;
  }) => <>{authorName} deleted cross filters</>,
  "activity.description.cross_filters_imported.title": ({
    authorName,
  }: {
    authorName: React.ReactNode;
  }) => <>{authorName} imported new cross filters </>,
  "activity.description.live_data_flow_management.title": ({
    authorName,
    active,
  }: {
    authorName: React.ReactNode;
    active: boolean;
  }) => {
    if (active) return <>{authorName} activated the live data flow</>;
    return <>{authorName} paused the live data flow</>;
  },
  "activity.event.badge.manual": "Manual",
  "activity.manual_failure"({ count }: { count: number }) {
    return count === 1 ? `1 non-modeled fault` : `${count} non-modeled faults`;
  },
  "activity.manual_fault_event_created.title": ({
    authorName,
    eventLink,
  }: {
    authorName: React.ReactNode;
    eventLink: React.ReactNode;
  }) => (
    <>
      {authorName} created the manual event {eventLink}
    </>
  ),
  "activity.manual_fault_event_dashboard_created.title": ({
    authorName,
    eventLink,
    dashboardLink,
  }: {
    authorName: React.ReactNode;
    eventLink: React.ReactNode;
    dashboardLink: React.ReactNode;
  }) => (
    <>
      {authorName} linked the manual event {eventLink} to dashboard {dashboardLink}
    </>
  ),
  "activity.manual_fault_event_dashboard_deleted.title": ({
    authorName,
    eventLink,
    dashboardLink,
  }: {
    authorName: React.ReactNode;
    eventLink: React.ReactNode;
    dashboardLink: React.ReactNode;
  }) => (
    <>
      {authorName} removed the link between the manual event {eventLink} and dashboard{" "}
      {dashboardLink}
    </>
  ),
  "activity.manual_fault_event_deleted.title": ({
    authorName,
    eventLink,
  }: {
    authorName: React.ReactNode;
    eventLink: React.ReactNode;
  }) => (
    <>
      {authorName} deleted the manual event {eventLink}
    </>
  ),
  "activity.manual_fault_event_status_updated.details.closed_at": ({
    previous,
    current,
  }: {
    previous: string;
    current: string;
  }) => `Closed at: ${previous} → ${current}`,
  "activity.manual_fault_event_status_updated.details.description": ({
    previous,
    current,
  }: {
    previous: string;
    current: string;
  }) => `Description: ${previous} → ${current}`,
  "activity.manual_fault_event_status_updated.details.identified_action": ({
    previous,
    current,
  }: {
    previous: string;
    current: string;
  }) => `Identified action: ${previous} → ${current}`,
  "activity.manual_fault_event_status_updated.details.identified_action_set": ({
    current,
  }: {
    current: string;
  }) => `Identified action: ${current}`,
  "activity.manual_fault_event_status_updated.details.name": ({
    previous,
    current,
  }: {
    previous: string;
    current: string;
  }) => `Name: ${previous} → ${current}`,
  "activity.manual_fault_event_status_updated.details.priority": ({
    previous,
    current,
  }: {
    previous: string;
    current: string;
  }) => `Priority: ${previous} → ${current}`,
  "activity.manual_fault_event_status_updated.details.reparability": ({
    previous,
    current,
  }: {
    previous: string;
    current: string;
  }) => `Reparability: ${previous} → ${current}`,
  "activity.manual_fault_event_status_updated.details.status": ({
    previous,
    current,
  }: {
    previous: string;
    current: string;
  }) => `Status: ${previous} → ${current}`,
  "activity.manual_fault_event_status_updated.title": ({
    authorName,
    eventLink,
  }: {
    authorName: React.ReactNode;
    eventLink: React.ReactNode;
  }) => (
    <>
      {authorName} edited the manual event {eventLink}
    </>
  ),
  "activity.manual_fault_event_time_window_created.title": ({
    authorName,
    eventLink,
    startDate,
  }: {
    authorName: React.ReactNode;
    eventLink: React.ReactNode;
    startDate: string;
  }) => (
    <>
      {authorName} added a new period starting on {startDate} to the manual event {eventLink}
    </>
  ),
  "activity.manual_fault_event_time_window_deleted.title": ({
    authorName,
    eventLink,
    startDate,
    endDate,
  }: {
    authorName: React.ReactNode;
    eventLink: React.ReactNode;
    startDate: string;
    endDate: string | null;
  }) => (
    <>
      {authorName} deleted the period{" "}
      {endDate ? `from ${startDate} to ${endDate}` : `starting on ${startDate}`} from the manual
      event {eventLink}
    </>
  ),
  "activity.manual_fault_event_time_window_updated.details.impact": ({
    indicatorName,
    value,
    unit,
  }: {
    indicatorName: string;
    value: number;
    unit: string | null;
  }) => (
    <>
      Impact on {indicatorName}: {value}
      {unit ? ` ${unit}` : ""}
    </>
  ),
  "activity.manual_fault_event_time_window_updated.details.start": ({
    startDate,
  }: {
    startDate: string;
  }) => <>Start date: {startDate}</>,
  "activity.manual_fault_event_time_window_updated.title": ({
    authorName,
    eventLink,
    startDate,
    endDate,
  }: {
    authorName: React.ReactNode;
    eventLink: React.ReactNode;
    startDate: string;
    endDate: string | null;
  }) => (
    <>
      {authorName} edited the period{" "}
      {endDate ? `from ${startDate} to ${endDate}` : `starting on ${startDate}`} in the manual event{" "}
      {eventLink}
    </>
  ),
  "activity.project_duplicated.title"({
    authorName,
    targetProjectLink,
  }: {
    authorName: React.ReactNode;
    targetProjectLink: React.ReactNode;
  }) {
    return (
      <>
        {authorName} duplicated project as project {targetProjectLink}
      </>
    );
  },

  "activity.project_duplicated_from.title"({
    authorName,
    sourceProjectLink,
  }: {
    authorName: React.ReactNode;
    sourceProjectLink: React.ReactNode;
  }) {
    return (
      <>
        {authorName} duplicated {sourceProjectLink} to this project
      </>
    );
  },
  "activity.project_manual_failure_created.title"({
    authorName,
    failureName,
  }: {
    authorName: React.ReactNode;
    failureName: string;
  }) {
    return (
      <>
        {authorName} created the non-modeled fault {failureName}
      </>
    );
  },
  "activity.project_manual_failure_deleted.title"({
    authorName,
    failureName,
  }: {
    authorName: React.ReactNode;
    failureName: string;
  }) {
    return (
      <>
        {authorName} deleted the non-modeled fault {failureName}
      </>
    );
  },
  "activity.selectFilter.analysis.manual_failure.changed":
    "Analysis - Management of non-modeled faults",
  "activity.selectFilter.failure_reintegrated": "Analysis - Fault integration",
  "activity.selectFilter.manual_fault_event.created": "Manual event - Creation",
  "activity.selectFilter.manual_fault_event.dashboard_created":
    "Manual event - Dashboard link - Creation",
  "activity.selectFilter.manual_fault_event.dashboard_deleted":
    "Manual event - Dashboard link - Deletion",
  "activity.selectFilter.manual_fault_event.deleted": "Manual event - Deletion",
  "activity.selectFilter.manual_fault_event.status_updated": "Manual event - Edition",
  "activity.selectFilter.manual_fault_event.time_window_created": "Manual event - Period addition",
  "activity.selectFilter.manual_fault_event.time_window_deleted": "Manual event - Period deletion",
  "activity.selectFilter.manual_fault_event.time_window_updated": "Manual event - Period edition",
  "activity.selectFilter.project_manual_failure.created":
    "Project - Creation of non-modeled faults",
  "activity.selectFilter.project_manual_failure.deleted":
    "Project - Deletion of non-modeled faults",
  "activity.selectFilter.project_manual_failure_metadata.changed":
    "Project - Edition of non-modeled faults",
  "activity.setup_diagnosis_algorithm_changed.details": ({
    prevPresetKey,
    currPresetKey,
    prevParameters,
    currParameters,
  }: {
    prevPresetKey?: string;
    currPresetKey?: string;
    prevParameters?: {
      key: string;
      value: string | number | boolean;
    }[];
    currParameters?: {
      key: string;
      value: string | number | boolean;
    }[];
  }) => (
    <>
      {prevPresetKey && currPresetKey && (
        <p>
          Modification of the version of {prevPresetKey} to {currPresetKey}
        </p>
      )}
      {!prevParameters && currParameters && (
        <>
          {currParameters.map(m => {
            if (typeof m.value === "boolean")
              return (
                <p key={m.key}>
                  {m.value === true ? "Selection" : "Deselection"} of parameter {m.key}
                </p>
              );
            return (
              <p key={m.key}>
                Declaration of a {m.key} parameter to {m.value}
              </p>
            );
          })}
        </>
      )}
      {prevParameters && currParameters && (
        <>
          {prevParameters.map(prev => {
            const currValue = currParameters.find(f => f.key === prev.key)?.value;
            if (typeof prev.value === "boolean")
              return (
                <p key={prev.key}>
                  Edit {prev.key} parameter from {prev.value ? "selected" : "deselected"} to{" "}
                  {currValue === undefined ? undefined : currValue ? "selected" : "deselected"}
                </p>
              );
            return (
              <p key={prev.key}>
                Edit {prev.key} parameter from {prev.value} to{" "}
                {currParameters.find(f => f.key === prev.key)?.value}
              </p>
            );
          })}
        </>
      )}
    </>
  ),
  "activity.setup_diagnosis_algorithm_changed.details.edited_parameter": (
    key: string,
    prevValue: string,
    currValue: string,
  ) => (
    <>
      Edit {key} parameter from {prevValue} to {currValue}
    </>
  ),
  "activity.setup_diagnosis_algorithm_changed.details.edited_preset": (
    presetOld: string,
    presetNew: string,
  ) => (
    <>
      Modification of the version of {presetOld} to {presetNew}
    </>
  ),
  "activity.setup_diagnosis_algorithm_changed.details.new_parameter": (
    key: string,
    value: number | boolean,
  ) => {
    if (typeof value === "boolean")
      return (
        <>
          {value === true ? "Selection" : "Deselection"} of parameter {key}
        </>
      );
    return (
      <>
        Declaration of a {key} parameter to {value}
      </>
    );
  },
  "activity.source_duplicated.range"({ from, to }: { from: string | null; to: string | null }) {
    if (from && to)
      return (
        <>
          {" "}
          from {from} to {to}
        </>
      );
    if (from) return <> from {from}</>;
    return <> until {to}</>;
  },
  "activity.source_duplicated.title"({
    originSourceId,
    targetSourceId,
    authorName,
  }: {
    originSourceId: string;
    targetSourceId: string;
    authorName: React.ReactNode;
  }) {
    return (
      <>
        {authorName} duplicated data source {originSourceId} as data source {targetSourceId}
      </>
    );
  },
  "analysis.publish.title": "Published",
  "analysis.unpublish.title": "Unpublished",
  "analysis_activated.title"({
    authorName,
    nbAnalysis,
  }: {
    authorName: React.ReactNode;
    nbAnalysis: number;
  }) {
    return (
      <>
        {authorName} created {nbAnalysis} {`${nbAnalysis > 1 ? "analyses" : "analysis"}`}
      </>
    );
  },
  "analysis_activation_filters_deleted.title"({ authorName }: { authorName: React.ReactNode }) {
    return <>{authorName} deleted the analysis filters</>;
  },
  "analysis_activation_filters_replaced.title"({
    authorName,
  }: // settingsLink,
  {
    authorName: React.ReactNode;
    // settingsLink: React.ReactNode;
  }) {
    return <>{authorName} imported new analysis filters</>;
  },
  "analysis_activation_frequency_changed.title"({
    authorName,
    frequency,
  }: {
    authorName: React.ReactNode;
    frequency: number;
  }) {
    const splittedInterval = minutesToFractional(frequency);

    if (frequency === 0)
      return <>{authorName} removed the constraint on the interval between two analyses</>;

    return (
      <>
        {authorName} set the interval between two analyses to{" "}
        {t("project.settings.analyses.frequency_value", splittedInterval)}
      </>
    );
  },
  "analysis_activation_reset.title"({ authorName }: { authorName: React.ReactNode }) {
    return <>{authorName} restarted the selection of analyses</>;
  },
  "analysis_deactivated.title"({
    authorName,
    nbAnalysis,
  }: {
    authorName: React.ReactNode;
    nbAnalysis: number;
  }) {
    return (
      <>
        {authorName} deleted {nbAnalysis} {`analys${nbAnalysis > 1 ? "es" : "is"}`}
      </>
    );
  },
  analysis_publication_changed: "Analysis - Edition of publication state",
  "analysis_publication_changed.title"({
    authorName,
    publishedCount,
    unpublishedCount,
  }: {
    authorName: React.ReactNode;
    publishedCount: number;
    unpublishedCount: number;
  }) {
    return (
      <>
        {authorName} modified the publication of {publishedCount + unpublishedCount}{" "}
        {publishedCount + unpublishedCount < 2 ? "analysis" : "analyses"}
      </>
    );
  },
  "category.project.type"(type: CategoryProjectActivityType): string {
    switch (type) {
      case "EQUIPMENT":
        return "equipments";
      case "SUSPECTED_ELEMENT":
        return "suspected elements";
      default:
        throw new Error(type satisfies never);
    }
  },
  "category.type"(type: CategoryActivityType, translation: Translation): string {
    switch (type) {
      case "PROJECT_TYPE":
        return translation.EN.TYPE;
      case "PROJECT_SITE":
        return "sites";
      case "FAILURE_CATEGORY":
        return "fault categories";
      case "DEGRADATION_TYPE":
        return "degradation types";
      case "EQUIPMENT_TYPE":
        return "equipment types";
      case "SYSTEM":
        return "systems";
      default:
        throw new Error(type satisfies never);
    }
  },
  "category_changed.title"({
    authorName,
    previousName,
    currentName,
    type,
    translation,
  }: {
    authorName: React.ReactNode;
    previousName: string;
    currentName: string;
    type: CategoryActivityType;
    translation: Translation;
  }) {
    return (
      <>
        {authorName} renamed the category {previousName} in {currentName} in the list of{" "}
        {en["category.type"](type, translation)}
      </>
    );
  },
  "category_created.title"({
    authorName,
    categoryName,
    type,
    translation,
  }: {
    authorName: React.ReactNode;
    categoryName: string;
    type: CategoryActivityType;
    translation: Translation;
  }) {
    return (
      <>
        {authorName} created the category {categoryName} in the list of{" "}
        {en["category.type"](type, translation)}
      </>
    );
  },
  "category_deleted.title"({
    authorName,
    categoryName,
    type,
    translation,
  }: {
    authorName: React.ReactNode;
    categoryName: string;
    type: CategoryActivityType;
    translation: Translation;
  }) {
    return (
      <>
        {authorName} deleted the category {categoryName} in the list of{" "}
        {en["category.type"](type, translation)}
      </>
    );
  },
  comment: "Comment: ",

  "comment_changed.title"({ authorName }: { authorName: React.ReactNode }) {
    return <>{authorName} edited a comment</>;
  },
  "comment_created.title"({ authorName }: { authorName: React.ReactNode }) {
    return <>{authorName} added a comment</>;
  },
  "comment_dashboard_created.title"(
    {
      authorName,
      dashboardLink,
    }: {
      authorName: React.ReactNode;
      dashboardLink: React.ReactNode;
    },
    translations: CustomTranslations,
  ) {
    return (
      <>
        {authorName} modified the content of the editable text block in the{" "}
        {translations.EN.DASHBOARD.toLowerCase()} {dashboardLink}
      </>
    );
  },
  "comment_deleted.title"({ authorName }: { authorName: React.ReactNode }) {
    return <>{authorName} deleted a comment</>;
  },
  "comment_edited.linked": ({
    analysisDate,
    faultEventName,
  }: {
    analysisDate: string | null;
    faultEventName: string | null;
  }) => {
    if (analysisDate !== null && faultEventName !== null) {
      return `Comment linked to analysis ${analysisDate} and to event ${faultEventName}`;
    } else if (analysisDate === null) {
      return `Comment linked to event ${faultEventName}`;
    } else {
      return `Comment linked to analysis ${analysisDate}`;
    }
  },
  comment_linked({
    analysisDate,
    faultEventName,
    dashboardName,
    translations,
  }: {
    analysisDate: string | null;
    faultEventName: string | null;
    dashboardName: string | null;
    translations: CustomTranslations;
  }) {
    if (analysisDate !== null && faultEventName !== null) {
      return `Comment linked to analysis ${analysisDate} and to event ${faultEventName}: `;
    } else if (analysisDate !== null) {
      return `Comment linked to analysis ${analysisDate}: `;
    } else if (dashboardName === null) {
      return `Comment linked to event ${faultEventName}: `;
    } else {
      return `Comment linked to ${translations.EN.DASHBOARD.toLowerCase()} ${dashboardName} : `;
    }
  },
  comment_new: "New comment: ",
  comment_old: "Old comment: ",
  "commons.analysis_title.group_and_label": ({ group, label }: { group: string; label: string }) =>
    `Cycle ${group} - Test ${label}`,
  "dashboard_changed.title"(
    {
      authorName,
      dashboardLink,
    }: {
      authorName: React.ReactNode;
      dashboardLink: React.ReactNode;
    },
    translations: CustomTranslations,
  ) {
    return (
      <>
        {authorName} edited the {translations.EN.DASHBOARD.toLowerCase()} {dashboardLink}
      </>
    );
  },
  dashboard_comment_new: "New content: ",
  dashboard_comment_old: "Previous content: ",
  "dashboard_created.title"(
    {
      authorName,
      dashboardLink,
    }: {
      authorName: React.ReactNode;
      dashboardLink: React.ReactNode;
    },
    translations: CustomTranslations,
  ) {
    return (
      <>
        {authorName} created the {translations.EN.DASHBOARD.toLowerCase()} {dashboardLink}
      </>
    );
  },
  "dashboard_deleted.title"(
    {
      authorName,
      dashboardName,
    }: {
      authorName: React.ReactNode;
      dashboardName: string;
    },
    translations: CustomTranslations,
  ) {
    return (
      <>
        {authorName} deleted the {translations.EN.DASHBOARD.toLowerCase()} {dashboardName}
      </>
    );
  },
  "dashboard_duplicated.title"(
    {
      authorName,
      sourceDashboardLink,
      targetDashboardLink,
    }: {
      authorName: React.ReactNode;
      sourceDashboardLink: React.ReactNode;
      targetDashboardLink: React.ReactNode;
    },
    translations: CustomTranslations,
  ) {
    return (
      <>
        {authorName} duplicated {translations.EN.DASHBOARD.toLowerCase()} {sourceDashboardLink} as{" "}
        {translations.EN.DASHBOARD.toLowerCase()} {targetDashboardLink}
      </>
    );
  },
  "dashboard_publication_changed.title"(
    {
      authorName,
      dashboardLink,
      published,
      readonly,
    }: {
      authorName: React.ReactNode;
      dashboardLink: React.ReactNode;
      published: boolean;
      readonly: boolean;
    },
    translations: CustomTranslations,
  ) {
    return (
      <>
        {authorName} {published ? "shared" : "made"} the {translations.EN.DASHBOARD.toLowerCase()}{" "}
        {dashboardLink} {published ? (readonly ? "in read only mode " : "") : ""}
        {published ? "" : "private"}
      </>
    );
  },
  "diagnosis_ended.title"({
    analysesLink,
    analysesCount,
  }: {
    analysesLink: React.ReactNode;
    analysesCount: number;
  }) {
    if (analysesCount === 1) {
      return <>{analysesLink} diagnosis has been completed</>;
    }
    return <>{analysesLink} diagnoses have been completed</>;
  },
  failure({ count }: { count: number }) {
    return count === 1 ? `1 fault` : `${count} faults`;
  },
  "failure_reparability_changed.title": ({
    authorName,
    failureLink,
    reparability,
    startDate,
    endDate,
    translation,
  }: {
    authorName: React.ReactNode;
    failureLink: React.ReactNode;
    reparability: Reparability | null;
    startDate: string;
    endDate: string;
    translation: {
      FR: {
        REPARABILITY_0: string;
        REPARABILITY_1: string;
        REPARABILITY_2: string;
      };
      EN: {
        REPARABILITY_0: string;
        REPARABILITY_1: string;
        REPARABILITY_2: string;
      };
    };
  }) => {
    const verb = reparability
      ? `declared outage type for repair ${en["reparability"](reparability, translation)} on`
      : "removed outage type for repair on";

    if (startDate === endDate) {
      return (
        <>
          {authorName} {verb} {failureLink} corresponding to {startDate}
        </>
      );
    }
    return (
      <>
        {authorName} {verb} {failureLink} between {startDate} and {endDate}
      </>
    );
  },
  "failure_status_changed.title"({
    authorName,
    failureLink,
    status,
    startDate,
    endDate,
  }: {
    authorName: React.ReactNode;
    failureLink: React.ReactNode;
    status: "SUSPECTED" | "CONFIRMED" | "FORCED_FALSE" | "REJECTED" | "LEGACY_REJECTED";
    startDate: string;
    endDate: string;
  }) {
    const verb =
      status === "CONFIRMED"
        ? "confirmed"
        : status === "REJECTED" || status === "FORCED_FALSE" || status === "LEGACY_REJECTED"
          ? "declared as false detection"
          : status === null
            ? "declared as to be analyzed"
            : "declared as suspected";

    const endVerb = status === "REJECTED" ? "and relaunched diagnosis" : "";

    if (startDate === endDate) {
      return (
        <>
          {authorName} {verb} {failureLink} corresponding to {startDate} {endVerb}
        </>
      );
    }
    return (
      <>
        {authorName} {verb} {failureLink} between {startDate} and {endDate} {endVerb}
      </>
    );
  },
  "fault_event_dashboard_created.title"(
    {
      authorName,
      faultEventLink,
      dashboardLink,
    }: {
      authorName: React.ReactNode;
      faultEventLink: React.ReactNode;
      dashboardLink: React.ReactNode;
    },
    translations: CustomTranslations,
  ) {
    return (
      <>
        {authorName} added link to {dashboardLink} {translations.EN.DASHBOARD.toLowerCase()} in{" "}
        {faultEventLink} event
      </>
    );
  },
  "fault_event_dashboard_deleted.title"(
    {
      authorName,
      faultEventLink,
      dashboardLink,
    }: {
      authorName: React.ReactNode;
      faultEventLink: React.ReactNode;
      dashboardLink: React.ReactNode;
    },
    translations: CustomTranslations,
  ) {
    return (
      <>
        {authorName} removed link to {dashboardLink} {translations.EN.DASHBOARD.toLowerCase()} in{" "}
        {faultEventLink} event
      </>
    );
  },
  "fault_event_diagnoses_status_changed.title": ({
    authorName,
    properties,
    faultEventLink,
  }: {
    authorName: React.ReactNode;
    properties: ActivityIndexJSON["FAULT_EVENT_DIAGNOSES_STATUS_CHANGED"]["properties"];
    faultEventLink: React.ReactNode;
  }) => {
    const status = properties.newDiagnosesStatus.current;

    let verb: string;
    switch (status) {
      case "CONFIRMED": {
        verb = "Confirmed";
        break;
      }
      case "SUSPECTED": {
        verb = "Suspected";
        break;
      }
      case "FORCED_FALSE":
      case "LEGACY_REJECTED": {
        verb = "False detection (without diagnostic relaunch)";
        break;
      }
      case "REJECTED": {
        verb = "False detection (with diagnostic relaunch)";
        break;
      }
      case null: {
        verb = "To be analyzed";
        break;
      }
      default:
        throw new Error(status satisfies never);
    }

    return (
      <>
        {authorName} has changed the status applied to future live detections for event{" "}
        {faultEventLink} to &quot;{verb}&quot;
      </>
    );
  },
  "fault_event_merge.title"({
    authorName,
    mergeEventLink,
    targetEventName,
    sourceEventName,
  }: {
    authorName: React.ReactNode;
    mergeEventLink: React.ReactNode;
    targetEventName: string;
    sourceEventName: string;
  }) {
    return (
      <>
        {authorName} merged events {targetEventName} and {sourceEventName} in {mergeEventLink}
      </>
    );
  },
  "fault_event_name_changed.title"({
    authorName,
    properties,
    faultEventLink,
  }: {
    authorName: React.ReactNode;
    properties: ActivityIndexJSON["FAULT_EVENT_NAME_CHANGED"]["properties"];
    faultEventLink: React.ReactNode;
  }) {
    return (
      <>
        {authorName} renamed event {properties.name.previous} in {faultEventLink}
      </>
    );
  },
  "fault_event_priority_changed.details"({
    previous,
    current,
  }: {
    previous: PriorityFaultEvent;
    current: PriorityFaultEvent;
  }) {
    const calculatedValue = "the calculated value";
    const previousValue = previous === null ? calculatedValue : getEventPriorityText(previous);
    const currentValue = current === null ? calculatedValue : getEventPriorityText(current);
    return `Priority changed from ${previousValue} to ${currentValue}`;
  },
  "fault_event_priority_changed.title"({
    authorName,
    faultEventLink,
  }: {
    authorName: React.ReactNode;
    faultEventLink: React.ReactNode;
  }) {
    return (
      <>
        {authorName} modified the priority of the event {faultEventLink}
      </>
    );
  },
  "fault_event_split.title"({
    authorName,
    originalEventLink,
    newSplitEventLink,
  }: {
    authorName: React.ReactNode;
    originalEventLink: React.ReactNode;
    newSplitEventLink: React.ReactNode;
  }) {
    return (
      <>
        {authorName} created event {newSplitEventLink} based on event {originalEventLink}
      </>
    );
  },
  "fault_event_status_changed.action": ({ action }: { action: string }) =>
    ` Identified actions: ${action}`,
  "fault_event_status_changed.detection": ({ detection }: { detection: boolean }) =>
    `Detection type: ${detection ? "true detection" : "false detection"}`,
  "fault_event_status_changed.priority": (priority: PriorityFaultEvent) =>
    `Priority: ${getEventPriorityText(priority)}`,
  "fault_event_status_changed.reparability": ({
    reparability,
    translation,
  }: {
    reparability: Reparability;
    translation: Translation;
  }) => `Reparability: ${en["reparability"](reparability, translation)}`,
  "fault_event_status_changed.status": ({
    previous,
    current,
  }: {
    previous: DenormalizedEventStatus;
    current: DenormalizedEventStatus;
  }) => `Status changed from "${getEventStatusText(previous)}" to "${getEventStatusText(current)}"`,
  "fault_event_status_changed.title"({
    authorName,
    faultEventLink,
  }: {
    authorName: React.ReactNode;
    faultEventLink: React.ReactNode;
  }) {
    return (
      <>
        {authorName} modified the status of the event {faultEventLink}
      </>
    );
  },
  "fault_reintegrated.title": ({
    authorName,
    failureLink,
    startDate,
    endDate,
  }: {
    authorName: React.ReactNode;
    failureLink: React.ReactNode;
    startDate: string;
    endDate: string;
  }) => {
    if (startDate === endDate) {
      return (
        <>
          {authorName} reintegrated {failureLink} in the diagnosis corresponding to {startDate}
        </>
      );
    }
    return (
      <>
        {authorName} reintegrated {failureLink} in the diagnosis between {startDate} and {endDate}
      </>
    );
  },

  "formula_quantity_created.title"({
    authorName,
    quantityName,
    formula,
  }: {
    authorName: React.ReactNode;
    quantityName: string;
    formula: string;
  }) {
    return (
      <>
        {authorName} created the calculated quantity {quantityName}, with the formula {formula}
      </>
    );
  },
  "lexical.quantities": "quantities",
  "lexical.settings": "settings",
  measure({ count }: { count: number }) {
    return count === 1 ? `1 measure` : `${count} measures`;
  },
  "measures_validity_changed.title"({
    authorName,
    action,
    measuresLink,
    firstDate,
    lastDate,
  }: {
    authorName: React.ReactNode;
    action: "VALIDATION" | "INVALIDATION";
    measuresLink: React.ReactNode;
    firstDate: string;
    lastDate: string;
  }) {
    const measureValidation = action === "VALIDATION" ? "validated" : "invalidated";
    if (firstDate === lastDate)
      return (
        <>
          {authorName} {measureValidation} {measuresLink} corresponding to {firstDate}
        </>
      );
    return (
      <>
        {authorName} {measureValidation} {measuresLink} between {firstDate} and {lastDate}
      </>
    );
  },
  "outdated.title"({ authorName, action }: { authorName: React.ReactNode; action: string }) {
    return (
      <>
        {authorName} has done action &quot;{action}&quot;
      </>
    );
  },
  "project_category_changed.title"({
    authorName,
    previousName,
    currentName,
    type,
  }: {
    authorName: React.ReactNode;
    previousName: string;
    currentName: string;
    type: CategoryProjectActivityType;
  }) {
    return (
      <>
        {authorName} renamed the category {previousName} in {currentName} in the list of{" "}
        {en["category.project.type"](type)}
      </>
    );
  },
  "project_category_created.title"({
    authorName,
    categoryName,
    type,
  }: {
    authorName: React.ReactNode;
    categoryName: string;
    type: CategoryProjectActivityType;
  }) {
    return (
      <>
        {authorName} created the category {categoryName} in the list of{" "}
        {en["category.project.type"](type)}
      </>
    );
  },
  "project_category_deleted.title"({
    authorName,
    categoryName,
    type,
  }: {
    authorName: React.ReactNode;
    categoryName: string;
    type: CategoryProjectActivityType;
  }) {
    return (
      <>
        {authorName} deleted the category {categoryName} in the list of{" "}
        {en["category.project.type"](type)}
      </>
    );
  },
  "project_changed.details.description"({
    previousDescription,
    currentDescription,
  }: {
    previousDescription: string;
    currentDescription: string;
  }) {
    return `The description ${previousDescription} has been changed to ${currentDescription}`;
  },
  "project_changed.details.description.add"({
    currentDescription,
  }: {
    currentDescription: string;
  }) {
    return `The description "${currentDescription}" has been added`;
  },
  "project_changed.details.description.deleted": "The description has been removed",
  "project_changed.details.description.modified"({
    currentDescription,
    previousDescription,
  }: {
    currentDescription: string;
    previousDescription: string;
  }) {
    return `The description has been changed from  "${previousDescription}" to "${currentDescription}"`;
  },
  "project_changed.details.name"({
    previousName,
    currentName,
  }: {
    previousName: string;
    currentName: string;
  }) {
    return `The name has been changed from ${previousName} to ${currentName}`;
  },
  "project_changed.details.production": "Project has been promoted to production",
  "project_changed.details.production.draft": "Project has been changed to research",
  "project_changed.details.site.add"({ currentSiteName }: { currentSiteName: string }) {
    return `The site "${currentSiteName}" has been added`;
  },
  "project_changed.details.site.deleted": "The site has been removed",
  "project_changed.details.site.modified"({
    currentSiteName,
    previousSiteName,
  }: {
    currentSiteName: string;
    previousSiteName: string;
  }) {
    return `The site has been changed from "${previousSiteName}" to "${currentSiteName}"`;
  },
  "project_changed.details.sourceId"({ previous, current }: { previous: string; current: string }) {
    return `The source has been changed from ${previous} to ${current}`;
  },
  "project_changed.details.timezone"({
    previousTimezone,
    currentTimezone,
  }: {
    previousTimezone: string;
    currentTimezone: string;
  }) {
    return `The timezone has been changed from ${previousTimezone} to ${currentTimezone}`;
  },
  "project_changed.details.type.add"({
    currentTypeName,
    translation,
  }: {
    currentTypeName: string;
    translation: {
      FR: {
        TYPE: string;
      };
      EN: {
        TYPE: string;
      };
    };
  }) {
    return `The ${translation.EN.TYPE} "${currentTypeName}" has been added`;
  },
  "project_changed.details.type.deleted"({
    translation,
  }: {
    translation: {
      FR: {
        TYPE: string;
      };
      EN: {
        TYPE: string;
      };
    };
  }) {
    return `The ${translation.EN.TYPE} has been removed`;
  },
  "project_changed.details.type.modified"({
    currentTypeName,
    previousTypeName,
    translation,
  }: {
    currentTypeName: string;
    previousTypeName: string;
    translation: {
      FR: {
        TYPE: string;
      };
      EN: {
        TYPE: string;
      };
    };
  }) {
    return `The ${translation.EN.TYPE} has been changed from "${previousTypeName}" to "${currentTypeName}"`;
  },
  "project_changed.title"({ authorName }: { authorName: React.ReactNode }) {
    return <>{authorName} edited attributes of the project</>;
  },
  "project_created.title"({ authorName }: { authorName: React.ReactNode }) {
    return <>{authorName} created the project</>;
  },
  "project_deleted.title"({
    authorName,
    projectName,
  }: {
    authorName: React.ReactNode;
    projectName: string;
  }) {
    return (
      <>
        {authorName} deleted the project {projectName}
      </>
    );
  },
  "project_manual_failure_metadata_changed.title"({
    authorName,
    failureName,
  }: {
    authorName: React.ReactNode;
    failureName: string;
  }) {
    return (
      <>
        {authorName} edited metadata of the non-modeled fault {failureName}
      </>
    );
  },
  "projects.modal.create_or_edit.input.type": (translation: Translation) => translation.EN.TYPE,
  "qois_changed.title"({
    authorName,
    qoiNames,
  }: {
    authorName: React.ReactNode;
    qoiNames: string[];
    count: number;
  }) {
    return qoiNames.length === 0 ? (
      <>{authorName} deleted the performance indicators</>
    ) : qoiNames.length === 1 ? (
      <>
        {authorName} changed the list of performance indicators: the new performance indicator is{" "}
        {qoiNames[0]}
      </>
    ) : (
      <>
        {authorName} changed the list of performance indicators: the new performance indicators are{" "}
        {qoiNames.join(", ")}
      </>
    );
  },
  "quantites_changed.details.description"({
    previousDescription,
    currentDescription,
  }: {
    previousDescription: string | null;
    currentDescription: string | null;
  }) {
    return `Edition of the description ${
      currentDescription === null && previousDescription !== null
        ? ` to an empty field`
        : previousDescription === null && currentDescription !== null
          ? ` with an addition to ${currentDescription}`
          : ` from ${previousDescription} to ${currentDescription}`
    }`;
  },
  "quantites_changed.details.equipmentType"({
    currentEquipmentTypeName,
    previousEquipmentTypeName,
  }: {
    currentEquipmentTypeName: string | null;
    previousEquipmentTypeName: string | null;
  }) {
    return `Edition of the equipment type ${
      currentEquipmentTypeName === null && previousEquipmentTypeName !== null
        ? ` to an empty field`
        : previousEquipmentTypeName === null && currentEquipmentTypeName !== null
          ? ` with an addition to ${currentEquipmentTypeName}`
          : ` from ${previousEquipmentTypeName} to ${currentEquipmentTypeName}`
    }`;
  },
  "quantites_changed.details.equipments"({
    currentEquipments,
    previousEquipments,
  }: {
    currentEquipments: string[];
    previousEquipments: string[];
  }) {
    return `Edition of equipment ${
      currentEquipments.length === 0 && previousEquipments.length > 0
        ? ` to an empty field`
        : previousEquipments.length === 0 && currentEquipments.length > 0
          ? ` with an addition to ${currentEquipments.join(",")}`
          : ` from ${previousEquipments.join(",")} to ${currentEquipments.join(",")}`
    }`;
  },
  "quantites_changed.details.formula"({
    previousFormula,
    currentFormula,
  }: {
    previousFormula: string | null;
    currentFormula: string | null;
  }) {
    return `Edition of the formula ${
      currentFormula === null && previousFormula !== null
        ? ` to an empty field`
        : previousFormula === null && currentFormula !== null
          ? ` with an addition to ${currentFormula}`
          : ` from ${previousFormula} to ${currentFormula}`
    }`;
  },
  "quantites_changed.details.formulaVersion.created"({
    startsAt,
    expression,
  }: {
    startsAt: string;
    expression: string;
  }) {
    return `Formula version starting on ${startsAt} created - Formula: ${expression}`;
  },
  "quantites_changed.details.formulaVersion.deleted"({
    startsAt,
    expression,
  }: {
    startsAt: string;
    expression: string;
  }) {
    return `Formula version starting on ${startsAt} deleted - Formula: ${expression}`;
  },
  "quantites_changed.details.formulaVersion.updated"({
    startsAt,
    expression,
  }: {
    startsAt: string;
    expression: string;
  }) {
    return `Formula version starting on ${startsAt} updated - Formula: ${expression}`;
  },
  "quantites_changed.details.max"({
    currentMax,
    previousMax,
  }: {
    currentMax: number | null;
    previousMax: number | null;
  }) {
    return `Edition of the max ${
      currentMax === null && previousMax !== null
        ? ` to an empty field`
        : previousMax === null && currentMax !== null
          ? ` with an addition to ${currentMax}`
          : ` from ${previousMax} to ${currentMax}`
    }`;
  },
  "quantites_changed.details.maxstd"({
    currentMaxstd,
    previousMaxstd,
  }: {
    currentMaxstd: number | null;
    previousMaxstd: number | null;
  }) {
    return `Edition of the max standard deviation ${
      currentMaxstd === null && previousMaxstd !== null
        ? ` to an empty field`
        : previousMaxstd === null && currentMaxstd !== null
          ? ` with an addition to ${currentMaxstd}`
          : ` from ${previousMaxstd} to ${currentMaxstd}`
    }`;
  },
  "quantites_changed.details.min"({
    currentMin,
    previousMin,
  }: {
    currentMin: number | null;
    previousMin: number | null;
  }) {
    return `Edition of the min ${
      currentMin === null && previousMin !== null
        ? ` to an empty field`
        : previousMin === null && currentMin !== null
          ? ` with an addition to ${currentMin}`
          : ` from ${previousMin} to ${currentMin}`
    }`;
  },
  "quantites_changed.details.name.modified"({
    currentName,
    previousName,
  }: {
    currentName: string | null;
    previousName: string | null;
  }) {
    return `Edition of the name from "${previousName}" to "${currentName}"`;
  },
  "quantites_changed.details.system"({
    currentSystemName,
    previousSystemName,
  }: {
    currentSystemName: string | null;
    previousSystemName: string | null;
  }) {
    return `Edition of the system ${
      currentSystemName === null && previousSystemName !== null
        ? ` to an empty field`
        : previousSystemName === null && currentSystemName !== null
          ? ` with an addition to ${currentSystemName}`
          : ` from ${previousSystemName} to ${currentSystemName}`
    }`;
  },
  "quantites_changed.details.unit"({
    previousUnit,
    currentUnit,
  }: {
    previousUnit: string | null;
    currentUnit: string | null;
  }) {
    return `Edition of the unit ${
      currentUnit === null && previousUnit !== null
        ? ` to an empty field`
        : previousUnit === null && currentUnit !== null
          ? ` with an addition to ${currentUnit}`
          : ` from ${previousUnit} to ${currentUnit}`
    }`;
  },
  "quantities_changed.title"({
    authorName,
    quantitiesLink,
    nbQuantities,
  }: {
    authorName: React.ReactNode;
    quantitiesLink: React.ReactNode;
    nbQuantities: number;
  }) {
    return (
      <>
        {authorName} edited {nbQuantities === 1 ? "a quantity" : "quantities"} in the page{" "}
        {quantitiesLink}
      </>
    );
  },
  "quantities_deleted.title"({
    authorName,
    count,
  }: {
    authorName: React.ReactNode;
    count: number;
  }) {
    return (
      <>
        {authorName} deleted {count} {count > 1 ? "quantities" : "quantity"}
      </>
    );
  },
  "quantities_replaced.title"({ authorName }: { authorName: React.ReactNode }) {
    return <>{authorName} imported new quantities</>;
  },
  related_analyses_deleted({ count }: { count: number }): string {
    return count < 2 ? "Related analysis has been deleted" : "Related analyses have been deleted";
  },
  reparability: (
    reparability: Reparability,
    translation: {
      FR: {
        REPARABILITY_0: string;
        REPARABILITY_1: string;
        REPARABILITY_2: string;
      };
      EN: {
        REPARABILITY_0: string;
        REPARABILITY_1: string;
        REPARABILITY_2: string;
      };
    },
  ): string => {
    switch (reparability) {
      case "REPARABILITY_0":
        return translation.EN.REPARABILITY_0;
      case "REPARABILITY_1":
        return translation.EN.REPARABILITY_1;
      case "REPARABILITY_2":
        return translation.EN.REPARABILITY_2;
      default:
        throw new Error(reparability satisfies never);
    }
  },
  "selectFilter.analysis.manual_failure.changed": "Analysis - Management of non-modeled faults",
  "selectFilter.analysis_activated": "Analysis - Creation of analyses",
  "selectFilter.analysis_activation_filters_deleted": "Settings - Analysis filters deletion",
  "selectFilter.analysis_activation_filters_replaced": "Settings - Analysis filters import",
  "selectFilter.analysis_activation_frenquency_changed":
    "Settings - Change of the interval between two analyses",
  "selectFilter.analysis_activation_reset": "Analysis - Reset the choice of analyses",
  "selectFilter.analysis_deactivated": "Analysis - Deletion of analyses",
  "selectFilter.category_changed": "Category - Edition",
  "selectFilter.category_created": "Category - Creation",
  "selectFilter.category_deleted": "Category - Deletion",
  "selectFilter.comment_changed": "Comment - Edition",
  "selectFilter.comment_created": "Comment - Creation",
  "selectFilter.comment_deleted": "Comment - Deletion",
  "selectFilter.cross_filters_deleted": "Quantity - Cross filters deletion",
  "selectFilter.cross_filters_imported": "Quantity - Cross filters import",
  "selectFilter.dashboard_changed": (translations: CustomTranslations) =>
    `${translations.EN.DASHBOARD} - Edition`,
  "selectFilter.dashboard_comment_created": (translations: CustomTranslations) =>
    `${translations.EN.DASHBOARD} - Modification of editable text block content`,
  "selectFilter.dashboard_created": (translations: CustomTranslations) =>
    `${translations.EN.DASHBOARD} - Creation`,
  "selectFilter.dashboard_deleted": (translations: CustomTranslations) =>
    `${translations.EN.DASHBOARD} - Deletion`,
  "selectFilter.dashboard_duplicated": (translations: CustomTranslations) =>
    `${translations.EN.DASHBOARD} - Duplication`,
  "selectFilter.dashboard_publication_changed": (translations: CustomTranslations) =>
    `${translations.EN.DASHBOARD} - Edition of sharing`,
  "selectFilter.diagnosis_ended": "Analysis - Diagnosis completed",
  "selectFilter.failure.reparability.changed": "Analysis - Edition of fault outage time for repair",
  "selectFilter.failures_status_changed": "Analysis - Edition of fault state",
  "selectFilter.fault_event_dashboard_created": (translations: CustomTranslations) =>
    `Event - Link with ${translations.EN.DASHBOARD.toLowerCase()} - Creation`,
  "selectFilter.fault_event_dashboard_deleted": (translations: CustomTranslations) =>
    `Event - Link with ${translations.EN.DASHBOARD.toLowerCase()} - Deletion`,
  "selectFilter.fault_event_diagnoses_status_changed":
    "Event - Change of status applied to future live detections",
  "selectFilter.fault_event_merge": "Event - Merge of events",
  "selectFilter.fault_event_name_changed": "Event - Edition of event name",
  "selectFilter.fault_event_priority_changed": "Event - Edition of event priority",
  "selectFilter.fault_event_split": "Event - Split of event",
  "selectFilter.fault_event_status_changed": "Event - Edition of event state",
  "selectFilter.formula_quantity_created": "Quantity - Creation of calculated quantity",
  "selectFilter.live_data_flow_management": "Project - Live data flow management",
  "selectFilter.measures_validity_changed": "Analysis - Edition of measure validity",
  "selectFilter.project_category_changed": "Project category - Edition",
  "selectFilter.project_category_created": "Project category - Creation",
  "selectFilter.project_category_deleted": "Project category - Deletion",
  "selectFilter.project_changed": "Project - Edition of attributes",
  "selectFilter.project_created": "Project - Creation",
  "selectFilter.project_deleted": "Project - Deletion",
  "selectFilter.project_duplicated": "Project - Duplication (source projet)",
  "selectFilter.project_duplicated_from": "Project - Duplication from (target project)",
  "selectFilter.project_manual_failure.created": "Project - Creation of non-modeled faults",
  "selectFilter.project_manual_failure_metadata.changed": "Project - Edition of non-modeled faults",
  "selectFilter.qois_changed": "Settings - Performance indicators",
  "selectFilter.quantities_changed": "Quantity - Quantities edition",
  "selectFilter.quantities_deleted": "Quantity - Quantities deletion",
  "selectFilter.quantities_replaced": "Quantity - Quantities import",
  "selectFilter.sensor_quantities_created": "Quantity - Sensor addition",
  "selectFilter.sensor_value_deleted": "Source - Data sensor deletion",
  "selectFilter.setup_changed": "Setup - Edition of attributes",
  "selectFilter.setup_created": "Setup - Creation",
  "selectFilter.setup_deleted": "Setup - Deletion",
  "selectFilter.setup_diagnosis_diagnosis_failures_changed": "Setup - Fault edition",
  "selectFilter.setup_diagnosis_diagnosis_failures_deleted": "Setup - Fault matrix deletion",
  "selectFilter.setup_diagnosis_failures_metadata_changed": "Setup - Fault metadata edition",
  "selectFilter.setup_diagnosis_failures_replaced": "Setup - Fault matrix import",
  "selectFilter.setup_diagnosis_observable_changed": "Setup - Edition of observables",
  "selectFilter.setup_diagnosis_observable_deleted": "Setup - Observables deletion",
  "selectFilter.setup_diagnosis_observable_replaced": "Setup -  Observables import",
  "selectFilter.setup_diagnosis_parameters_changed": "Setup - Algorithm parameter edition",
  "selectFilter.setup_diagnosis_preset_changed": "Setup - Algorithm edition",
  "selectFilter.setup_duplicated": "Setup - Duplication",
  "selectFilter.setup_simulation_input_deleted": "Setup - Model inputs deletion",
  "selectFilter.setup_simulation_input_replaced": "Setup - Model inputs import",
  "selectFilter.setup_simulation_model_deleted": "Setup - Model deletion",
  "selectFilter.setup_simulation_model_replaced": "Setup - Model import",
  "selectFilter.setup_simulation_model_status_checked": "Setup - Model verification",
  "selectFilter.setup_simulation_offset_changed": "Setup - Edition of offsets",
  "selectFilter.setup_simulation_output_deleted": "Setup - Model outputs deletion",
  "selectFilter.setup_simulation_output_replaced": "Setup - Model outputs import",
  "selectFilter.simulation_ended": "Analysis - Simulation completed",
  "selectFilter.snapshot_upserted": "Source - Data import",
  "selectFilter.snapshots_deleted": "Source - Data deletion",
  "selectFilter.source.created": "Source - Source creation",
  "selectFilter.source.deleted": "Source - Source deletion",
  "selectFilter.source_duplicated": "Source - Duplication",
  "sensor_quantities_created.title"({
    authorName,
    count,
  }: {
    authorName: React.ReactNode;
    count: number;
  }) {
    return (
      <>
        {authorName} added {count} sensor{count > 1 ? "s" : ""}
      </>
    );
  },
  "sensor_value_deleted.title"({
    authorName,
    sensorTag,
    startPeriod,
    endPeriod,
    sourceLink,
  }: {
    authorName: React.ReactNode;
    sensorTag: string;
    startPeriod: string;
    endPeriod: string;
    sourceLink: React.ReactNode;
  }) {
    return (
      <>
        {authorName} deleted the data of sensor {sensorTag} from {startPeriod} to {endPeriod} in the{" "}
        source {sourceLink}
      </>
    );
  },
  "setupDiagnosisFailuresMetatada_changed.details.degradationType"({
    previousDegradationTypeName,
    currentDegradationTypeName,
  }: {
    previousDegradationTypeName: string | null;
    currentDegradationTypeName: string | null;
  }) {
    return `Edition of the degradation type ${
      !currentDegradationTypeName && previousDegradationTypeName
        ? ` to an empty field`
        : !previousDegradationTypeName && currentDegradationTypeName
          ? ` with an addition to ${currentDegradationTypeName}`
          : ` from ${previousDegradationTypeName} to ${currentDegradationTypeName}`
    }`;
  },
  "setupDiagnosisFailuresMetatada_changed.details.description"({
    previousDescription,
    currentDescription,
  }: {
    previousDescription: string | null;
    currentDescription: string | null;
  }) {
    return `Edition of the description ${
      !currentDescription && previousDescription
        ? ` to an empty field`
        : !previousDescription && currentDescription
          ? ` with an addition to ${currentDescription}`
          : ` from ${previousDescription} to ${currentDescription}`
    }`;
  },
  "setupDiagnosisFailuresMetatada_changed.details.displayName"({
    previousDisplayName,
    currentDisplayName,
  }: {
    previousDisplayName: string;
    currentDisplayName: string;
  }) {
    return `Edition of the display name from ${previousDisplayName} to ${currentDisplayName}`;
  },
  "setupDiagnosisFailuresMetatada_changed.details.equipmentType"({
    previousEquipmentTypeName,
    currentEquipmentTypeName,
  }: {
    previousEquipmentTypeName: string | null;
    currentEquipmentTypeName: string | null;
  }) {
    return `Edition of the equipment type ${
      !currentEquipmentTypeName && previousEquipmentTypeName
        ? ` to an empty field`
        : !previousEquipmentTypeName && currentEquipmentTypeName
          ? ` with an addition to ${currentEquipmentTypeName}`
          : ` from ${previousEquipmentTypeName} to ${currentEquipmentTypeName}`
    }`;
  },
  "setupDiagnosisFailuresMetatada_changed.details.equipments"({
    currentEquipments,
    previousEquipments,
  }: {
    currentEquipments: string[];
    previousEquipments: string[];
  }) {
    return `Edition of equipment ${
      currentEquipments.length === 0 && previousEquipments.length > 0
        ? ` to an empty field`
        : previousEquipments.length === 0 && currentEquipments.length > 0
          ? ` with an addition to ${currentEquipments.join(",")}`
          : ` from ${previousEquipments.join(",")} to ${currentEquipments.join(",")}`
    }`;
  },
  "setupDiagnosisFailuresMetatada_changed.details.failureCategory"({
    previousFailureCategoryName,
    currentFailureCategoryName,
  }: {
    previousFailureCategoryName: string | null;
    currentFailureCategoryName: string | null;
  }) {
    return `Edition of the failure category ${
      !currentFailureCategoryName && previousFailureCategoryName
        ? ` to an empty field`
        : !previousFailureCategoryName && currentFailureCategoryName
          ? ` with an addition to ${currentFailureCategoryName}`
          : ` from ${previousFailureCategoryName} to ${currentFailureCategoryName}`
    }`;
  },
  "setupDiagnosisFailuresMetatada_changed.details.name.modified"({
    currentName,
    previousName,
  }: {
    currentName: string;
    previousName: string;
  }) {
    return `Edition of the name from "${previousName}" to "${currentName}"`;
  },
  "setupDiagnosisFailuresMetatada_changed.details.reparability"({
    previousReparability,
    currentReparability,
    translation,
  }: {
    previousReparability: Reparability | null;
    currentReparability: Reparability | null;
    translation: {
      FR: {
        REPARABILITY_0: string;
        REPARABILITY_1: string;
        REPARABILITY_2: string;
      };
      EN: {
        REPARABILITY_0: string;
        REPARABILITY_1: string;
        REPARABILITY_2: string;
      };
    };
  }) {
    return `Edition of the outage time for repair ${
      currentReparability === null && previousReparability !== null
        ? ` to an empty field`
        : previousReparability === null && currentReparability !== null
          ? ` with an addition to ${en["reparability"](currentReparability, translation)}`
          : ` from ${
              previousReparability
                ? en["reparability"](previousReparability, translation)
                : t("reparability.none")
            } to ${
              currentReparability
                ? en["reparability"](currentReparability, translation)
                : t("reparability.none")
            }`
    }`;
  },
  "setupDiagnosisFailuresMetatada_changed.details.suspectedElements"({
    currentSuspectedElements,
    previousSuspectedElements,
  }: {
    currentSuspectedElements: string[];
    previousSuspectedElements: string[];
  }) {
    return `Edition of suspected elements ${
      currentSuspectedElements.length === 0 && previousSuspectedElements.length > 0
        ? ` to an empty field`
        : previousSuspectedElements.length === 0 && currentSuspectedElements.length > 0
          ? ` with an addition to ${currentSuspectedElements.join(",")}`
          : ` from ${previousSuspectedElements.join(",")} to ${currentSuspectedElements.join(",")}`
    }`;
  },
  "setupDiagnosisFailuresMetatada_changed.details.system"({
    previousSystemName,
    currentSystemName,
  }: {
    previousSystemName: string | null;
    currentSystemName: string | null;
  }) {
    return `Edition of the system ${
      !currentSystemName && previousSystemName
        ? ` to an empty field`
        : !previousSystemName && currentSystemName
          ? ` with an addition to ${currentSystemName}`
          : ` from ${previousSystemName} to ${currentSystemName}`
    }`;
  },
  "setupDiagnosisFailuresMetatada_changed.details.unit"({
    previousUnit,
    currentUnit,
  }: {
    previousUnit: string | null;
    currentUnit: string | null;
  }) {
    return `Edition of the unit ${
      !currentUnit && previousUnit
        ? ` to an empty field`
        : !previousUnit && currentUnit
          ? ` with an addition to ${currentUnit}`
          : ` from ${previousUnit} to ${currentUnit}`
    }`;
  },
  "setup_changed.title"({
    authorName,
    setupLink,
  }: {
    authorName: React.ReactNode;
    setupLink: React.ReactNode;
  }) {
    return (
      <>
        {authorName} edited {setupLink} attributes
      </>
    );
  },
  "setup_created.title"({
    authorName,
    setupLink,
  }: {
    authorName: React.ReactNode;
    setupLink: React.ReactNode;
  }) {
    return (
      <>
        {authorName} created the {setupLink}
      </>
    );
  },
  "setup_deleted.title"({
    authorName,
    setupName,
  }: {
    authorName: React.ReactNode;
    setupName: React.ReactNode;
  }) {
    return (
      <>
        {authorName} deleted setup {setupName}
      </>
    );
  },
  "setup_diagnosis_failures_deleted.title"({
    authorName,
    setupLink,
  }: {
    authorName: React.ReactNode;
    setupLink: React.ReactNode;
  }) {
    return (
      <>
        {authorName} removed the failures of the {setupLink}
      </>
    );
  },
  "setup_diagnosis_failures_replaced.title"({
    authorName,
    setupLink,
  }: {
    authorName: React.ReactNode;
    setupLink: React.ReactNode;
  }) {
    return (
      <>
        {authorName} imported new faults in {setupLink}
      </>
    );
  },
  "setup_diagnosis_parameters_changed.title"({
    authorName,
    setupLink,
    presetName,
  }: {
    authorName: React.ReactNode;
    setupLink: React.ReactNode;
    presetName: string;
  }) {
    return (
      <>
        {authorName} edited the {presetName} algorithm settings in the {setupLink}
      </>
    );
  },
  "setup_diagnosis_preset_changed.title"({
    authorName,
    setupLink,
    prevPreset,
    currPreset,
  }: {
    authorName: React.ReactNode;
    setupLink: React.ReactNode;
    prevPreset: string;
    currPreset: string;
  }) {
    return (
      <>
        {authorName} has changed the algorithm type from {prevPreset} to {currPreset} in the{" "}
        {setupLink}
      </>
    );
  },
  "setup_duplicated.title"({
    authorName,
    sourceSetupLink,
    targetSetupLink,
  }: {
    authorName: React.ReactNode;
    sourceSetupLink: React.ReactNode;
    targetSetupLink: React.ReactNode;
  }) {
    return (
      <>
        {authorName} duplicated {sourceSetupLink} as {targetSetupLink}
      </>
    );
  },
  "setup_failures_changed.title"({
    authorName,
    setupLink,
    name,
    active,
  }: {
    authorName: React.ReactNode;
    active: boolean;
    setupLink: React.ReactNode;
    name: React.ReactNode;
  }) {
    return (
      <>
        {authorName} {active ? " activated" : " deactivated"} the fault {name} of {setupLink}
      </>
    );
  },
  "setup_failures_metadata_changed.title"({
    authorName,
    setupLink,
    name,
  }: {
    authorName: React.ReactNode;
    setupLink: React.ReactNode;
    name: string;
  }) {
    return (
      <>
        {authorName} edited the metadata of the fault {name} in the {setupLink}
      </>
    );
  },
  "setup_observable_changed.title"({
    authorName,
    setupLink,
    properties,
  }: {
    authorName: React.ReactNode;
    setupLink: React.ReactNode;
    properties: ActivityIndexJSON["SETUP_DIAGNOSIS_OBSERVABLES_CHANGED"]["properties"];
  }) {
    return properties.current.active === undefined ? (
      <>
        {authorName} edited the sigma of the observable {properties.observableName} from{" "}
        {properties.previous.sigma} to {properties.current.sigma} in the {setupLink}
      </>
    ) : (
      <>
        {authorName} {properties.current.active ? " activated" : " deactivated"} observable{" "}
        {properties.observableName} of {setupLink}
      </>
    );
  },
  "setup_observable_deleted.title"({
    authorName,
    setupLink,
  }: {
    authorName: React.ReactNode;
    setupLink: React.ReactNode;
  }) {
    return (
      <>
        {authorName} removed the observables from the {setupLink}
      </>
    );
  },
  "setup_observable_replaced.title"({
    authorName,
    setupLink,
  }: {
    authorName: React.ReactNode;
    setupLink: React.ReactNode;
  }) {
    return (
      <>
        {authorName} imported new observables in {setupLink}
      </>
    );
  },
  "setup_simulation_inputs.deleted.title"({
    authorName,
    setupLink,
  }: {
    authorName: React.ReactNode;
    setupLink: React.ReactNode;
  }) {
    return (
      <>
        {authorName} deleted the model inputs from the {setupLink}
      </>
    );
  },
  "setup_simulation_inputs.replaced.title"({
    authorName,
    setupLink,
  }: {
    authorName: React.ReactNode;
    setupLink: React.ReactNode;
  }) {
    return (
      <>
        {authorName} imported new model inputs in {setupLink}
      </>
    );
  },
  "setup_simulation_model_deleted.title"({
    authorName,
    setupLink,
  }: {
    authorName: React.ReactNode;
    setupLink: React.ReactNode;
  }) {
    return (
      <>
        {authorName} deleted the model from the {setupLink}
      </>
    );
  },
  "setup_simulation_model_replaced.title"({
    authorName,
    setupLink,
  }: {
    authorName: React.ReactNode;
    setupLink: React.ReactNode;
  }) {
    return (
      <>
        {authorName} imported a new model in {setupLink}
      </>
    );
  },
  "setup_simulation_model_status_checked.title"({
    setupLink,
    validated,
  }: {
    setupLink: React.ReactNode;
    validated: boolean;
  }) {
    return (
      <>
        Simulation setup has been {validated ? "validated" : "rejected"} for the {setupLink}
      </>
    );
  },
  "setup_simulation_offset_changed.title"({
    authorName,
    setupLink,
    properties,
  }: {
    authorName: React.ReactNode;
    setupLink: React.ReactNode;
    properties: ActivityIndexJSON["SETUP_SIMULATION_OFFSET_CHANGED"]["properties"];
  }) {
    return (
      <>
        {authorName} edited the offset of the quantity {properties.quantityName} from{" "}
        {properties.offsetOld} {properties.unit ? properties.unit : ""} to {properties.offsetNew}{" "}
        {properties.unit ? properties.unit : ""} in the {setupLink}
      </>
    );
  },
  "setup_simulation_outputs.deleted.title"({
    authorName,
    setupLink,
  }: {
    authorName: React.ReactNode;
    setupLink: React.ReactNode;
  }) {
    return (
      <>
        {authorName} removed the model outputs from the {setupLink}
      </>
    );
  },
  "setup_simulation_outputs.replaced.title"({
    authorName,
    setupLink,
  }: {
    authorName: React.ReactNode;
    setupLink: React.ReactNode;
  }) {
    return (
      <>
        {authorName} imported new model outputs in {setupLink}
      </>
    );
  },
  "simulation_ended.title"({
    analysesLink,
    analysesCount,
  }: {
    analysesLink: React.ReactNode;
    analysesCount: number;
  }) {
    if (analysesCount === 1) {
      return <>{analysesLink} simulation has been completed</>;
    }
    return <>{analysesLink} simulations have been completed</>;
  },
  "snapshots_deleted.title"({
    authorName,
    snapshotCount,
    sourceLink,
  }: {
    authorName: React.ReactNode;
    snapshotCount: number;
    sourceLink: React.ReactNode;
  }) {
    return (
      <>
        {authorName} deleted {snapshotCount} {`${snapshotCount > 1 ? "data points" : "data point"}`}{" "}
        in the source {sourceLink}{" "}
      </>
    );
  },
  "source_deleted.title"({
    authorName,
    sourceName,
  }: {
    authorName: React.ReactNode;
    sourceName: string;
  }) {
    return (
      <>
        {authorName} deleted the source {sourceName}
      </>
    );
  },
  "source_upserted.title"({
    authorName,
    nbPoints,
    sourceLink,
    isSourceCreated,
  }: {
    authorName: React.ReactNode;
    nbPoints: number;
    sourceLink: React.ReactNode;
    isSourceCreated: boolean;
  }) {
    return (
      <>
        {authorName} imported {nbPoints} {`${nbPoints > 1 ? "data points" : "data point"}`} in the
        source {sourceLink} {isSourceCreated ? " (new source)" : ""}
      </>
    );
  },
  "unknown.title"({ authorName, action }: { authorName: React.ReactNode; action: string }) {
    return (
      <>
        {authorName} has done &quot;{action}&quot;
      </>
    );
  },
};

export default en;
